import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AuftragsbestaetigungFromAngebotDialog } from "@/pages/dokumente/AuftragsbestaetigungFromAngebotDialog";
import {
  AuftragsbestaetigungFromAngebotContext,
  type AuftragsbestaetigungFromAngebotContextValue,
  type AuftragsbestaetigungFromAngebotOpenOptions,
} from "@/pages/dokumente/auftragsbestaetigungFromAngebotContextValue";
import {
  OPEN_AUFTRAGSBESTAETIGUNG_FROM_ANGEBOT_EVENT,
  type AuftragsbestaetigungFromAngebotEventDetail,
} from "@/pages/dokumente/auftragsbestaetigungFromAngebotEvents";

export function AuftragsbestaetigungFromAngebotProvider({
  children,
  renderDialog = true,
}: {
  children: ReactNode;
  renderDialog?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [angebotId, setAngebotId] = useState<number | null>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setAngebotId(null);
  }, []);

  const open = useCallback((options: AuftragsbestaetigungFromAngebotOpenOptions) => {
    if (!Number.isFinite(options.angebotId) || options.angebotId <= 0) return;
    setAngebotId(options.angebotId);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    function handleEvent(event: Event) {
      const detail = (event as CustomEvent<AuftragsbestaetigungFromAngebotEventDetail>).detail;
      if (!detail?.angebotId) return;
      open({ angebotId: detail.angebotId });
    }

    window.addEventListener(OPEN_AUFTRAGSBESTAETIGUNG_FROM_ANGEBOT_EVENT, handleEvent);
    return () => {
      window.removeEventListener(OPEN_AUFTRAGSBESTAETIGUNG_FROM_ANGEBOT_EVENT, handleEvent);
    };
  }, [open]);

  const value = useMemo<AuftragsbestaetigungFromAngebotContextValue>(
    () => ({ open, close, isOpen, angebotId }),
    [open, close, isOpen, angebotId],
  );

  return (
    <AuftragsbestaetigungFromAngebotContext.Provider value={value}>
      {children}
      {renderDialog ? (
        <AuftragsbestaetigungFromAngebotDialog open={isOpen} angebotId={angebotId} onClose={close} />
      ) : null}
    </AuftragsbestaetigungFromAngebotContext.Provider>
  );
}
